/**
 * Panel Configuration API Client
 *
 * Handles CRUD operations for user panel configurations.
 * Enables persistence of UI state (panel positions, sizes, visibility) across sessions.
 *
 * @version 1.0.0
 * @date January 12, 2026
 * @task T-S23-005
 */

import { z } from 'zod';

// ============================================================================
// TYPES & SCHEMAS
// ============================================================================

export const PanelTypeSchema = z.enum([
  'simulation_control',
  'attack_event_log',
  'network_legend',
  'app_communication',
  'device_details',
  'threat_details',
  'policy_details',
  'console_manager',
  'system_logs',
  'custom',
]);

export type PanelType = z.infer<typeof PanelTypeSchema>;

export const PanelConfigurationSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  panel_id: z.string(),
  panel_type: PanelTypeSchema,
  position_x: z.number().int(),
  position_y: z.number().int(),
  width: z.number().int().min(100).max(2000),
  height: z.number().int().min(50).max(1500),
  is_visible: z.boolean(),
  is_minimized: z.boolean(),
  is_maximized: z.boolean(),
  z_index: z.number().int(),
  config: z.record(z.string(), z.unknown()),
  view_context: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type PanelConfiguration = z.infer<typeof PanelConfigurationSchema>;

export const UpsertPanelConfigRequestSchema = z.object({
  panel_id: z.string(),
  panel_type: PanelTypeSchema,
  position_x: z.number().int().default(50),
  position_y: z.number().int().default(50),
  width: z.number().int().min(100).max(2000).default(400),
  height: z.number().int().min(50).max(1500).default(300),
  is_visible: z.boolean().default(true),
  is_minimized: z.boolean().default(false),
  is_maximized: z.boolean().default(false),
  z_index: z.number().int().default(100),
  config: z.record(z.string(), z.unknown()).default({}),
  view_context: z.string().default('network'),
});

export type UpsertPanelConfigRequest = z.infer<typeof UpsertPanelConfigRequestSchema>;

export const PanelLayoutTemplateSchema = z.object({
  id: z.string().uuid(),
  template_name: z.string(),
  description: z.string().optional(),
  view_context: z.string(),
  is_default: z.boolean(),
  panels: z.array(z.record(z.string(), z.unknown())),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type PanelLayoutTemplate = z.infer<typeof PanelLayoutTemplateSchema>;

// ============================================================================
// API BASE URL
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_MDC_API_URL || '';

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      return parsed.state?.token?.accessToken || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(
  method: string,
  endpoint: string,
  body?: unknown
): Promise<T> {
  const token = getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API request failed: ${response.status}`);
  }
  
  return response.json();
}

/**
 * List all panel configurations for the current user
 */
export async function listPanelConfigs(viewContext?: string): Promise<PanelConfiguration[]> {
  const query = viewContext ? `?view_context=${encodeURIComponent(viewContext)}` : '';
  return apiRequest<PanelConfiguration[]>('GET', `/api/v1/panel-config${query}`);
}

/**
 * Get a specific panel configuration
 */
export async function getPanelConfig(panelId: string, viewContext: string): Promise<PanelConfiguration> {
  return apiRequest<PanelConfiguration>(
    'GET',
    `/api/v1/panel-config/${encodeURIComponent(panelId)}/${encodeURIComponent(viewContext)}`
  );
}

/**
 * Create or update a panel configuration
 */
export async function upsertPanelConfig(request: UpsertPanelConfigRequest): Promise<PanelConfiguration> {
  const validated = UpsertPanelConfigRequestSchema.parse(request);
  return apiRequest<PanelConfiguration>('PUT', '/api/v1/panel-config', validated);
}

/**
 * Batch create or update multiple panel configurations
 */
export async function batchUpsertPanelConfigs(
  panels: UpsertPanelConfigRequest[]
): Promise<{ saved: number; panels: PanelConfiguration[] }> {
  const validated = panels.map((p) => UpsertPanelConfigRequestSchema.parse(p));
  return apiRequest<{ saved: number; panels: PanelConfiguration[] }>(
    'PUT',
    '/api/v1/panel-config/batch',
    { panels: validated }
  );
}

/**
 * Delete a panel configuration
 */
export async function deletePanelConfig(panelId: string, viewContext: string): Promise<void> {
  await apiRequest<{ deleted: boolean }>(
    'DELETE',
    `/api/v1/panel-config/${encodeURIComponent(panelId)}/${encodeURIComponent(viewContext)}`
  );
}

/**
 * Delete all panel configurations for a view
 */
export async function deleteAllPanelsForView(viewContext: string): Promise<{ count: number }> {
  return apiRequest<{ deleted: boolean; count: number }>(
    'DELETE',
    `/api/v1/panel-config/view/${encodeURIComponent(viewContext)}`
  );
}

/**
 * List available panel layout templates
 */
export async function listTemplates(viewContext?: string): Promise<PanelLayoutTemplate[]> {
  const query = viewContext ? `?view_context=${encodeURIComponent(viewContext)}` : '';
  return apiRequest<PanelLayoutTemplate[]>('GET', `/api/v1/panel-config/templates${query}`);
}

/**
 * Apply a template to the user's panel configuration
 */
export async function applyTemplate(
  templateId: string
): Promise<{ success: boolean; template: string; view_context: string }> {
  return apiRequest<{ success: boolean; template: string; view_context: string }>(
    'POST',
    `/api/v1/panel-config/apply-template/${encodeURIComponent(templateId)}`
  );
}

/**
 * Reset panel configuration to default template
 */
export async function resetToDefault(
  viewContext: string
): Promise<{ success: boolean; template: string; view_context: string }> {
  return apiRequest<{ success: boolean; template: string; view_context: string }>(
    'POST',
    `/api/v1/panel-config/reset/${encodeURIComponent(viewContext)}`
  );
}

// ============================================================================
// LOCAL STORAGE FALLBACK (for demo mode / offline)
// ============================================================================

const LOCAL_STORAGE_KEY = 'panel-configurations';

/**
 * Get panel configurations from localStorage (demo mode fallback)
 */
export function getLocalPanelConfigs(viewContext?: string): PanelConfiguration[] {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return [];
    
    const configs: PanelConfiguration[] = JSON.parse(stored);
    
    if (viewContext) {
      return configs.filter((c) => c.view_context === viewContext);
    }
    
    return configs;
  } catch {
    return [];
  }
}

/**
 * Save panel configurations to localStorage (demo mode fallback)
 */
export function setLocalPanelConfigs(configs: PanelConfiguration[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(configs));
  } catch {
    // Ignore quota errors
  }
}

/**
 * Upsert a single panel config in localStorage
 */
export function upsertLocalPanelConfig(request: UpsertPanelConfigRequest): PanelConfiguration {
  const existing = getLocalPanelConfigs();
  const now = new Date().toISOString();
  
  const existingIndex = existing.findIndex(
    (c) => c.panel_id === request.panel_id && c.view_context === request.view_context
  );
  
  const existingConfig = existingIndex >= 0 ? existing[existingIndex] : undefined;
  
  const config: PanelConfiguration = {
    id: existingConfig?.id ?? crypto.randomUUID(),
    user_id: 'local-user',
    panel_id: request.panel_id,
    panel_type: request.panel_type,
    position_x: request.position_x ?? 50,
    position_y: request.position_y ?? 50,
    width: request.width ?? 400,
    height: request.height ?? 300,
    is_visible: request.is_visible ?? true,
    is_minimized: request.is_minimized ?? false,
    is_maximized: request.is_maximized ?? false,
    z_index: request.z_index ?? 100,
    config: request.config ?? {},
    view_context: request.view_context ?? 'network',
    created_at: existingConfig?.created_at ?? now,
    updated_at: now,
  };
  
  if (existingIndex >= 0) {
    existing[existingIndex] = config;
  } else {
    existing.push(config);
  }
  
  setLocalPanelConfigs(existing);
  return config;
}

/**
 * Delete a panel config from localStorage
 */
export function deleteLocalPanelConfig(panelId: string, viewContext: string): void {
  const existing = getLocalPanelConfigs();
  const filtered = existing.filter(
    (c) => !(c.panel_id === panelId && c.view_context === viewContext)
  );
  setLocalPanelConfigs(filtered);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  listPanelConfigs,
  getPanelConfig,
  upsertPanelConfig,
  batchUpsertPanelConfigs,
  deletePanelConfig,
  deleteAllPanelsForView,
  listTemplates,
  applyTemplate,
  resetToDefault,
  getLocalPanelConfigs,
  setLocalPanelConfigs,
  upsertLocalPanelConfig,
  deleteLocalPanelConfig,
};
